/* eslint-disable  react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect, useRef } from 'react'
import {
  SidePanelContainer,
  Button,
  ButtonVariant,
  MaterialIcon,
  MaterialIconSize,
  FilterPanel,
  ButtonModifiers,
  useMediaQuery,
  MEDIA_QUERIES
} from '@ampcore/design-system'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { useFeatureFlag } from '@ampcore/feature-flags-react'
import xss from 'xss'
import ResourceCardGrid from './ResourceCardGrid'
import {
  getSubFilterArray,
  buildSubTopicStringWithDetails,
  buildUrl
} from './queryBuilders'
import { FeatureFlag } from '@ampcore/ampcom-contracts'
import {
  ResourceService,
  ResourceResultItem
} from '@ampcore/client-side-services'
import './resource-center.css'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'

import { StoredOption, SubTopicItem, ValueListResponse } from './types'

const parseSessionArray = <T,>(key: string): T[] => {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as T[]) : []
  } catch {
    return []
  }
}

export const ResourceCenter: React.FC = () => {
  const [storedOptions, setStoredOptions] = useState<StoredOption[]>(
    parseSessionArray<StoredOption>('resourceCenterFilters')
  )
  const [optionsLoaded, setOptionsLoaded] = useState(false)

  const isSmall = useMediaQuery(MEDIA_QUERIES.sm)
  const isTablet = useMediaQuery(MEDIA_QUERIES.md)

  const firstRender = useRef(true)
  const initialSearchDispatched = useRef(false)
  const requestVersion = useRef(0)

  // get local storage or use empty array as default
  const [storedResults, setStoredResults] = useState<ResourceResultItem[]>([])

  const searchParams = new URLSearchParams(document.location.search)
  const paramCategory = searchParams.get('category')

  const [error, setError] = useState<unknown | null>(null)
  const [filterError, setFilterError] = useState<unknown | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const sortBy = '-ampcom:sortingPriorityInt'
  const [offset, setOffset] = useState<string | number>(
    searchParams.get('offset') || 0
  )
  const [limit] = useState<string | number>(searchParams.get('s') || 6)
  const [totalSize, setTotalSize] = useState<number | null>(null)
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)
  const [subTopicData, setSubTopicData] = useState<SubTopicItem[]>(
    parseSessionArray<SubTopicItem>('resourceCenterSubFilters')
  )

  const [isFilters, setIsFilters] = useState(true)
  const { flagValue: isWatsonSearch, isReady: isWatsonFlagReady } =
    useFeatureFlag(FeatureFlag.EXPERIMENT_WATSON_SEARCH_RESOURCE_CENTER, false)

  const atb = useMemo(
    () => new AnalyticsTagBuilder().setDefaultName('resource-center-results'),
    []
  )

  useEffect(() => {
    if (!isWatsonFlagReady) return

    if (subTopicData.length) {
      if (firstRender.current) {
        firstRender.current = false
        if (paramCategory && paramCategory.length) {
          const arrayUrlCategory = paramCategory.split(',')

          if (subTopicData.length) {
            const updatedData = applyCategorySelection(
              arrayUrlCategory,
              subTopicData
            )
            setSubTopicData(updatedData)
            setOptionsLoaded(true)
            initialSearchDispatched.current = true
            sendRequest(updatedData)
            return
          }
        }
        if (!initialSearchDispatched.current) {
          initialSearchDispatched.current = true
          setOptionsLoaded(true)
          sendRequest()
        }
      }
    } else if (!optionsLoaded) {
      getDefaultTopics()
    }
  }, [sortBy, optionsLoaded, isWatsonSearch, isWatsonFlagReady])

  const applyCategorySelection = (
    arrayUrlCategory: string[],
    subTopicDataInput: SubTopicItem[]
  ): SubTopicItem[] => {
    return subTopicDataInput.map(parent => {
      const parentSelected = arrayUrlCategory.includes(parent.id)
      const updatedChildren = (parent.subItems ?? []).map(child => ({
        ...child,
        checked: parentSelected || arrayUrlCategory.includes(child.id)
      }))
      const hasSelectedChild = updatedChildren.some(child => child.checked)

      return {
        ...parent,
        defaultExpanded: parentSelected || hasSelectedChild,
        subItems: updatedChildren
      }
    })
  }

  const buildCollapsedFilterArray = (
    subTopicDataInput: SubTopicItem[],
    subFiltersArray: string[]
  ): string[] => {
    if (!subTopicDataInput?.length) return []

    const selectedSet = new Set(subFiltersArray)
    const result: string[] = []

    subTopicDataInput.forEach(parent => {
      if (selectedSet.has(parent.id)) {
        result.push(parent.id)
        return
      }

      const children = parent.subItems || []

      const allChildrenSelected =
        children.length > 0 &&
        children.every(child => selectedSet.has(child.id))

      if (allChildrenSelected) {
        result.push(parent.id)
      } else {
        children.forEach(child => {
          if (selectedSet.has(child.id)) {
            result.push(child.id)
          }
        })
      }
    })

    return result
  }

  const formatId = (str: string): string => {
    return str
      .toLowerCase()
      .replace(/&amp;/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-)|(-$)/g, '')
  }

  const transformData = (
    parentData: StoredOption[],
    childData: ValueListResponse
  ): SubTopicItem[] => {
    const listItems = childData._embedded?.documents?.[0]?.listitem || []

    const grouped = listItems.reduce<Record<string, Array<{ label: string }>>>(
      (acc, item) => {
        if (!acc[item.key]) acc[item.key] = []
        acc[item.key].push(item)
        return acc
      },
      {}
    )

    return parentData.map(parent => {
      const children = grouped[parent.key] || []

      return {
        id: parent.key,
        label: parent.value,
        defaultExpanded: parent.checked,
        subItems: children.map(child => ({
          id: formatId(child.label),
          label: child.label,
          checked: parent.checked
        }))
      }
    })
  }

  const handleSubFilterSubmit = () => {
    setOffset(0)
    setStoredResults([])
    setTotalSize(null)
    sendRequest(null, 0)
  }

  const clearAllFilters = () => {
    const cleared = subTopicData.map(parent => ({
      ...parent,
      subItems: (parent.subItems ?? []).map(child => ({
        ...child,
        checked: false
      }))
    }))
    setSubTopicData(cleared)
  }

  const handleShowFilters = () => {
    setIsFilters(true)
  }

  const handleFilterHide = (
    hasFilter: boolean,
    filterData: SubTopicItem[]
  ): void => {
    setSubTopicData(filterData)
    setIsFilters(hasFilter)
  }

  const showFilterButton = (
    <Button
      variant={ButtonVariant.LINK}
      className="FilterPanel-showFilters u-flex u-flexAlignItemsCenter u-md-hidden u-sm-hidden u-marginRight20"
      onClick={handleShowFilters}
      {...atb.dataAnalyticAttribute('show-filters')}
    >
      <MaterialIcon
        icon={AmpcoreMaterialIconId.TUNE}
        className="u-textColorNeutral u-marginRight8"
        size={MaterialIconSize.SIZE_20}
      />
      <u>Show filters</u>
    </Button>
  )

  const filterPanelButton = (
    <div className="FilterPanel-buttonGroup u-flexGrow1">
      <Button
        variant={ButtonVariant.PRIMARY}
        modifiers={[ButtonModifiers.BLOCK]}
        className="u-marginTop16"
        onClick={handleSubFilterSubmit}
        isLoading={isLoading}
        disabled={isLoading}
      >
        Update resources
      </Button>
      <Button
        modifiers={[ButtonModifiers.BLOCK]}
        variant={ButtonVariant.SECONDARY}
        className="u-marginTop8"
        onClick={clearAllFilters}
        disabled={isLoading}
        {...atb.dataAnalyticAttribute('clear-all-filters')}
      >
        Clear all filters
      </Button>
    </div>
  )

  const showFiltersButton = (
    <Button
      variant={ButtonVariant.LINK}
      className="FilterPanel-showFilters u-flex u-flexAlignItemsCenter"
      onClick={() => setMobileFilterOpen(true)}
      {...atb.dataAnalyticAttribute('show-filters')}
    >
      <MaterialIcon
        icon={AmpcoreMaterialIconId.TUNE}
        className="u-textColorNeutral u-marginRight8"
        size={MaterialIconSize.SIZE_20}
      />
      <u>Show filters</u>
    </Button>
  )

  const sidePanelSubFilter = (
    <SidePanelContainer
      isOpen={mobileFilterOpen}
      minimizable={false}
      heading={'Filters'}
      handleCustomClose={() => setMobileFilterOpen(false)}
      footer={filterPanelButton}
      stickyFooter
      className="ResourceCenter-subFilterPanel"
    >
      {optionsLoaded && subTopicData.length > 0 && (
        <FilterPanel
          data={subTopicData}
          title="Filters"
          onChange={setSubTopicData}
          handleHideFilters={handleFilterHide}
          isSidePanelFilter={true}
          className="u-marginLeft24 u-marginRight24 u-flexGrow1"
        />
      )}
    </SidePanelContainer>
  )

  const getDefaultTopics = async () => {
    await fetch(
      '/halapi/selection:valuelist?_scope=/content/documents/administration/value-lists/ameriprise/resource-center/sub-topic/financial-goals'
    )
      .then(res => res.json())
      .then(
        result => {
          setFilterError(null)
          const newOptions: StoredOption[] = []
          result._embedded.documents[0].listitem.map(
            (item: { key: string; label: string }) => {
              newOptions.push({
                key: item.key,
                value: item.label,
                checked: false
              })
            }
          )
          if (newOptions.length) {
            if (paramCategory && paramCategory.length) {
              const arrayUrlCategory = paramCategory.split(',')

              const copyOptions = [...newOptions]

              copyOptions.forEach(item => {
                if (arrayUrlCategory.includes(item.key)) {
                  item.checked = true
                } else {
                  item.checked = false
                }
              })
            }
            setStoredOptions(newOptions)
            const topics = JSON.parse(JSON.stringify(newOptions))
            getSubTopicDetails(topics)
            return
          }
          setFilterError(error)
        },
        errDefaultTopics => {
          setFilterError(errDefaultTopics)
        }
      )
  }

  const getSubTopicDetails = async (topics: StoredOption[]): Promise<void> => {
    const url =
      '/halapi/selection:valuelist?_scope=/content/documents/administration/value-lists/ameriprise/resource-center/sub-filters'
    await fetch(url)
      .then(res => res.json())
      .then(
        result => {
          setFilterError(null)
          const subFiltersData = transformData(topics, result)
          const urlCategories = paramCategory ? paramCategory.split(',') : []
          const initializedSubFiltersData = urlCategories.length
            ? applyCategorySelection(urlCategories, subFiltersData)
            : subFiltersData

          if (initializedSubFiltersData.length) {
            setSubTopicData(initializedSubFiltersData)
            setOptionsLoaded(true)
            initialSearchDispatched.current = true
            sendRequest(initializedSubFiltersData)
            return
          }
          setFilterError(error)
        },
        errDefaultTopics => {
          setFilterError(errDefaultTopics)
        }
      )
  }

  const isValidUrl = (url: string): boolean => {
    try {
      const parsedUrl = new URL(url, window.location.origin)
      return (
        parsedUrl.origin === window.location.origin &&
        !parsedUrl.protocol.includes('file')
      )
    } catch {
      return false
    }
  }

  const sendRequest = async (
    overrideSubTopicData: SubTopicItem[] | null = null,
    overrideOffset: number | string | null = null
  ): Promise<void> => {
    const effectiveOffset = overrideOffset ?? offset
    const watsonOffset = Number(effectiveOffset) || 0
    const normalizedOffset = Number(effectiveOffset) || 0
    const requestId = ++requestVersion.current

    setMobileFilterOpen(false)
    setError(null)
    setIsLoading(true)
    if (isWatsonSearch ? watsonOffset === 0 : normalizedOffset === 0) {
      setStoredResults([])
    }
    const activeSubTopicData = overrideSubTopicData ?? subTopicData
    const subFilterArray = getSubFilterArray(activeSubTopicData)
    const subTopicString = buildSubTopicStringWithDetails(
      subFilterArray,
      activeSubTopicData
    )
    const url = encodeURI(
      buildUrl(subTopicString, sortBy, normalizedOffset, limit)
    )
    if (isWatsonSearch) {
      const parentIdSet = new Set(activeSubTopicData.map(item => item.id))
      const selectedSubTopics: string[] = []
      const selectedSubTopicDetails: string[] = []

      subFilterArray.forEach((id: string) => {
        if (parentIdSet.has(id)) {
          selectedSubTopics.push(id)
        } else {
          selectedSubTopicDetails.push(id)
        }
      })

      try {
        const result = await ResourceService.fetchResourceData(
          selectedSubTopics,
          selectedSubTopicDetails,
          watsonOffset,
          ' ',
          6
        )
        if (requestId !== requestVersion.current) return

        if (!result) {
          setIsLoading(false)
          setError('Failed to load resource center data')
          return
        }

        const normalizedResults: ResourceResultItem[] = result?.items ?? []

        setIsLoading(false)
        setStoredResults(prevStoredResults => [
          ...prevStoredResults,
          ...normalizedResults
        ])

        setTotalSize(
          prevTotalSize =>
            result?.matchingResults ??
            (watsonOffset === 0 ? 0 : (prevTotalSize ?? 0))
        )

        sessionStorage.setItem(
          'resourceCenterSubFilters',
          JSON.stringify(activeSubTopicData)
        )
      } catch {
        setIsLoading(false)
        setError('Failed to load resource center data')
      }
    } else {
      if (!isValidUrl(url)) {
        console.error('Blocked request to unapproved URL')
        return
      }

      await fetch(url)
        .then(res => res.json())
        .then(
          result => {
            if (requestId !== requestVersion.current) return
            setTimeout(() => {
              setIsLoading(false)
            }, 1000)

            if (normalizedOffset > 0) {
              setStoredResults(prevStoredResults => [
                ...prevStoredResults,
                ...result._embedded.documents
              ])
            } else {
              setStoredResults(result._embedded.documents)
            }
            setTotalSize(result._meta.totalSize)
            sessionStorage.setItem(
              'resourceCenterSubFilters',
              JSON.stringify(activeSubTopicData)
            )
            sessionStorage.setItem(
              'resourceCenterFilters',
              JSON.stringify(storedOptions)
            )
            if (!result._meta.totalSize) setError('0 results found')
          },
          errSendRequest => {
            if (requestId !== requestVersion.current) return
            setTimeout(() => {
              setIsLoading(false)
            }, 1000)
            setError(errSendRequest)
          }
        )
    }
    if (requestId !== requestVersion.current) return
    const collapsedFilterArray = buildCollapsedFilterArray(
      activeSubTopicData,
      subFilterArray
    )
    const categoryParamStr = collapsedFilterArray.length
      ? 'category=' + collapsedFilterArray.join('%2C')
      : ''
    let paramStr = categoryParamStr
    paramStr = paramStr.length ? '?' + paramStr : ''
    const newUrl = window.location.origin + window.location.pathname + paramStr

    // updating url so filtered results are shareable
    history.replaceState(null, '', xss(newUrl))
  }

  const loadMoreButtonClick = (newOffset: number | string): void => {
    if (isWatsonSearch) {
      const parsedNewOffset = Number(newOffset) || 0
      const pageSize = Number(limit) || 6
      const currentOffset = Number(offset) || 0
      const nextOffset =
        parsedNewOffset > currentOffset
          ? parsedNewOffset
          : currentOffset + pageSize

      setOffset(nextOffset)
      sendRequest(null, nextOffset)
      return
    }

    setOffset(newOffset)
    sendRequest(null, newOffset)
  }

  if (filterError) {
    return (
      <div
        className="Notification Notification--withIcon Notification--alert u-marginTop32 jserror"
        id="ResourceCenterError"
        role="alert"
      >
        <div className="Notification-content">
          <p>Sorry, something went wrong while getting your resources.</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="ResourceCenter-headingRow u-sm-marginBottom16 u-marginBottom24">
        <h1>Your Resources</h1>
        {isSmall && showFiltersButton}
      </div>

      <div className="Grid Grid--withGutter card-grid-component u-marginBottom48 resource-center results-page">
        {subTopicData.length > 0 && isFilters && (
          <div className="filterBar u-size1of4 u-md-hidden u-sm-hidden u-borderRight u-paddingRight32">
            <FilterPanel
              data={subTopicData}
              title="Filters"
              onChange={setSubTopicData}
              handleHideFilters={handleFilterHide}
            />
            {filterPanelButton}
          </div>
        )}
        <div
          className={`results ${
            isFilters
              ? 'u-lg-size3of4 u-paddingLeft32 u-md-paddingLeft8 u-sm-paddingLeft8'
              : ''
          } u-sizeFull`}
        >
          {error ? (
            <>
              {!isFilters && showFilterButton}
              <div
                className="Notification Notification--withIcon Notification--alert u-marginTop32 jserror"
                id="ResourceCenterError"
                role="alert"
              >
                <div className="Notification-content">
                  <p>
                    Sorry, something went wrong while getting your resources.
                  </p>
                </div>
              </div>
            </>
          ) : (
            storedResults && (
              <div>
                <div className="u-lg-flex u-md-flex u-flexAlignItemsCenter u-marginBottom20">
                  {!isFilters && showFilterButton}
                  {isTablet && (
                    <span className="u-marginRight20 u-flex u-flexAlignItemsCenter">
                      {showFiltersButton}
                    </span>
                  )}
                  {typeof totalSize === 'number' && (
                    <p className="ResourceCenter-resultsText u-margin0">
                      We found <strong>{totalSize} resources</strong> that match
                      your search criteria.
                    </p>
                  )}
                </div>
                {optionsLoaded && (
                  <ResourceCardGrid
                    results={storedResults}
                    isWatsonSearch={isWatsonSearch}
                    isFilters={isFilters}
                    isLoading={isLoading}
                    totalSize={totalSize}
                  />
                )}
                {(totalSize ?? 0) - storedResults.length > 0 && !isLoading && (
                  <div className="u-flex u-flexJustifyCenter u-marginTop24">
                    <Button
                      variant={ButtonVariant.SECONDARY}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault()
                        loadMoreButtonClick(storedResults.length)
                      }}
                    >
                      Load more
                    </Button>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </div>
      {sidePanelSubFilter}
    </>
  )
}
